import React from 'react';
import { motion } from 'motion/react';
import PixelIcon from './PixelIcon';

export interface CurrencyDisplayProps {
  coins: number;
  gems: number;
  lives: number;
  streak?: number;
  canClaimStreak?: boolean;
  variant?: 'header' | 'mobile' | 'detailed' | 'compact';
  showStreak?: boolean;
  animated?: boolean;
}

interface CurrencyChip {
  id: 'coins' | 'gems' | 'lives' | 'streak';
  icon: 'coin' | 'gem' | 'life' | 'fire';
  value: number;
  label: string;
}

function formatValue(value: number) {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}m`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return value.toLocaleString();
}

const CurrencyDisplay: React.FC<CurrencyDisplayProps> = ({
  coins,
  gems,
  lives,
  streak = 0,
  canClaimStreak = false,
  variant = 'header',
  showStreak = false,
  animated = true,
}) => {
  const chips: CurrencyChip[] = [
    { id: 'coins', icon: 'coin', value: coins, label: 'Coins' },
    { id: 'gems', icon: 'gem', value: gems, label: 'Gems' },
    { id: 'lives', icon: 'life', value: lives, label: 'Lives' },
  ];

  if (showStreak) {
    chips.push({ id: 'streak', icon: 'fire', value: streak, label: 'Streak' });
  }

  return (
    <div className={`currency-strip currency-strip--${variant}`} role="group" aria-label="Player resources">
      {chips.map((chip, index) => (
        <motion.div
          key={chip.id}
          className={`currency-chip currency-chip--${chip.id}`}
          initial={animated ? { opacity: 0, y: 10, scale: 0.94 } : undefined}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={animated ? { duration: 0.24, delay: index * 0.04 } : { duration: 0 }}
          whileHover={animated ? { y: -1, scale: 1.01 } : undefined}
        >
          {chip.id === 'streak' && canClaimStreak && (
            <span className="currency-chip__badge" aria-label="Reward available">
              !
            </span>
          )}
          <span className="currency-chip__icon" aria-hidden="true">
            <PixelIcon name={chip.icon} size={16} />
          </span>
          <span className="currency-chip__value">{formatValue(chip.value)}</span>
          <span className="currency-chip__label">{chip.label}</span>
        </motion.div>
      ))}
    </div>
  );
};

export default CurrencyDisplay;
